import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
    CheckCircleIcon,
    XCircleIcon,
    EyeIcon,
    EyeSlashIcon,
    LockClosedIcon,
    ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { Layout } from '@components';
import {
    isSectionVisible,
    applyDependencyRules,
    findLockingMaster,
    getDependentsOf,
    SECTION_ITEMS,
    isItemVisible,
    SectionItem,
    isSectionDefaultable,
    SECTION_DEFAULT_VALUES,
} from '@data/configuratorSections';
import { ADMIN_SECTION_GROUPS, mirrorVestCategories, mirrorVestItems, mirrorVestValues } from '@data/vestSettings';
import { SECTION_LABELS, ITEM_LABELS } from '@data/configuratorLabels';
import {
    getAdminSettings,
    updateAdminVisibleSections,
    updateAdminVisibleItems,
    updateAdminVisibleFabrics,
    updateAdminSectionDefaults,
    updateAdminEmbroiderySettings,
    getAllAdminLiasses,
    AdminLiasse,
} from '@services/admin.service';
import { getButtonCatalog, ButtonCategory } from '@services/buttons.service';
import { getLiningCatalog, LiningCategory } from '@services/linings.service';
import { getCollarFeltCatalog, CollarFeltCategory } from '@services/collarFelts.service';
import { getContrastColorCatalog, ContrastColorOption } from '@services/contrastColors.service';

const labelSection = (key?: string, fallback?: string) => (key ? SECTION_LABELS[key] || fallback || key : fallback || '');
const labelItem = (key?: string, fallback?: string) => (key ? ITEM_LABELS[key] || fallback || key : fallback || '');

const BUTTON_SECTION_KEY = 'perso.buttons';
const PANTS_BUTTON_SECTION_KEY = 'perso.pantsButtons';
const VEST_BUTTON_SECTION_KEYS = ['perso.vestButtons', 'vest.buttons'];
const JACKET_LINING_SECTION_KEY = 'perso.jacketLining';
const PANTS_LINING_SECTION_KEY = 'perso.pantsLiningFabric';
const VEST_BACK_LINING_SECTION_KEYS = ['coupe.vestBackFabric', 'vest.backFabric'];
const LINING_SECTION_KEYS = [JACKET_LINING_SECTION_KEY, PANTS_LINING_SECTION_KEY, ...VEST_BACK_LINING_SECTION_KEYS];
const COLLAR_FELT_SECTION_KEY = 'perso.collarFelt';
const CONTRAST_SECTION_KEYS = ['perso.jacketContrast', 'perso.pantsContrast', 'perso.vestContrast', 'vest.contrast'];
type CatalogCategory = ButtonCategory | LiningCategory | CollarFeltCategory;
const isCatalogSection = (sectionKey: string): boolean =>
    [BUTTON_SECTION_KEY, PANTS_BUTTON_SECTION_KEY, ...VEST_BUTTON_SECTION_KEYS, ...LINING_SECTION_KEYS, COLLAR_FELT_SECTION_KEY, ...CONTRAST_SECTION_KEYS].includes(sectionKey);
const buttonCategoryVisibilityKey = (sectionKey: string, categoryId: string): string =>
    sectionKey === BUTTON_SECTION_KEY ? categoryId : `${sectionKey}:${categoryId}`;
const BUTTON_CATEGORY_LABELS: Record<string, string> = {
    corozo: 'Corozo',
    metal: 'Métal',
    real_horn: 'Corne véritable',
    resin: 'Résine',
    shell: 'Nacre',
    shirt: 'Chemise',
};

const labelCatalogCategory = (category: CatalogCategory): string =>
    ('label' in category && category.label) || BUTTON_CATEGORY_LABELS[category.id] || category.id.replace(/_/g, ' ');

export const VisibilitySettings: FC = () => {
    const [loading, setLoading] = useState(true);
    const [serverSections, setServerSections] = useState<Record<string, boolean>>({});
    const [serverItems, setServerItems] = useState<Record<string, Record<string, boolean>>>({});
    const [serverButtonCategories, setServerButtonCategories] = useState<Record<string, boolean>>({});
    const [buttonCatalog, setButtonCatalog] = useState<ButtonCategory[]>([]);
    const [liningCatalog, setLiningCatalog] = useState<LiningCategory[]>([]);
    const [collarFeltCatalog, setCollarFeltCatalog] = useState<CollarFeltCategory[]>([]);
    const [contrastColorCatalog, setContrastColorCatalog] = useState<ContrastColorOption[]>([]);
    const contrastCategory = useMemo(() => [{ id: 'CONTRAST_COLORS', label: 'Couleurs de contraste', options: contrastColorCatalog }], [contrastColorCatalog]);

    const catalogForSection = useCallback((sectionKey: string): CatalogCategory[] =>
        CONTRAST_SECTION_KEYS.includes(sectionKey) ? contrastCategory
            : sectionKey === COLLAR_FELT_SECTION_KEY ? collarFeltCatalog
            : LINING_SECTION_KEYS.includes(sectionKey) ? liningCatalog : buttonCatalog,
    [buttonCatalog, liningCatalog, collarFeltCatalog, contrastCategory]);

    // Items par section, avec les boutons issus du catalogue backend (partagé front + admin).
    const sectionItems = useMemo<Record<string, SectionItem[]>>(() => {
        const buttons: SectionItem[] = [
            { id: 'none', labelKey: 'configurator.option.button.default' },
            { id: 'recouvert', label: 'Recouvert (tissu du costume)' },
            ...buttonCatalog.flatMap((cat) => cat.options.map((o) => ({ id: o.id, label: o.id }))),
        ];
        return {
            ...SECTION_ITEMS,
            [BUTTON_SECTION_KEY]: buttons,
            [PANTS_BUTTON_SECTION_KEY]: buttons,
            [VEST_BUTTON_SECTION_KEYS[0]]: buttons,
            [VEST_BUTTON_SECTION_KEYS[1]]: buttons,
            [JACKET_LINING_SECTION_KEY]: [
                { id: 'match', label: 'Assortie au costume' },
                ...liningCatalog.flatMap((cat) => cat.options.map((o) => ({ id: o.id, label: o.id }))),
            ],
            [PANTS_LINING_SECTION_KEY]: [
                { id: 'match', label: 'Assortie au costume' },
                ...liningCatalog.flatMap((cat) => cat.options.map((o) => ({ id: o.id, label: o.id }))),
            ],
            [VEST_BACK_LINING_SECTION_KEYS[0]]: [
                ...(SECTION_ITEMS[VEST_BACK_LINING_SECTION_KEYS[0]] || []),
                ...liningCatalog.flatMap((cat) => cat.options.map((o) => ({ id: o.id, label: o.id }))),
            ],
            [VEST_BACK_LINING_SECTION_KEYS[1]]: [
                ...(SECTION_ITEMS[VEST_BACK_LINING_SECTION_KEYS[1]] || []),
                ...liningCatalog.flatMap((cat) => cat.options.map((o) => ({ id: o.id, label: o.id }))),
            ],
            [COLLAR_FELT_SECTION_KEY]: [
                { id: 'match', label: 'Assorti au costume' },
                { id: 'other', label: 'Personnalisé' },
                ...collarFeltCatalog.flatMap((cat) => cat.options.map((o) => ({ id: o.id, label: o.id }))),
            ],
            ...Object.fromEntries(CONTRAST_SECTION_KEYS.map((key) => [key, [
                ...(SECTION_ITEMS[key] || []),
                ...contrastColorCatalog.map((option) => ({ id: option.id, label: option.name })),
            ]])),
        };
    }, [buttonCatalog, liningCatalog, collarFeltCatalog, contrastColorCatalog]);

    const initial = useMemo<Record<string, boolean>>(() => {
        const map: Record<string, boolean> = {};
        for (const group of ADMIN_SECTION_GROUPS) {
            for (const section of group.sections) {
                map[section.key] = isSectionVisible(section.key, serverSections);
            }
        }
        return map;
    }, [serverSections]);

    const initialItems = useMemo<Record<string, Record<string, boolean>>>(() => {
        const map: Record<string, Record<string, boolean>> = {};
        for (const [sectionKey, items] of Object.entries(sectionItems)) {
            map[sectionKey] = {};
            for (const item of items) {
                map[sectionKey][item.id] = isItemVisible(sectionKey, item.id, serverItems);
            }
        }
        return map;
    }, [serverItems, sectionItems]);

    const initialButtonCategories = useMemo<Record<string, boolean>>(() => {
        const map: Record<string, boolean> = { ...serverButtonCategories };
        for (const sectionKey of [BUTTON_SECTION_KEY, PANTS_BUTTON_SECTION_KEY, ...VEST_BUTTON_SECTION_KEYS, ...LINING_SECTION_KEYS, COLLAR_FELT_SECTION_KEY, ...CONTRAST_SECTION_KEYS]) {
            for (const category of catalogForSection(sectionKey)) {
                const key = buttonCategoryVisibilityKey(sectionKey, category.id);
                map[key] = serverButtonCategories[key] !== false;
            }
        }
        return map;
    }, [catalogForSection, serverButtonCategories]);

    const [draft, setDraft] = useState<Record<string, boolean>>({});
    const [itemsDraft, setItemsDraft] = useState<Record<string, Record<string, boolean>>>({});
    const [buttonCategoriesDraft, setButtonCategoriesDraft] = useState<Record<string, boolean>>({});
    const [serverDefaults, setServerDefaults] = useState<Record<string, string>>({});
    const [serverEmbroideryMaxCharacters, setServerEmbroideryMaxCharacters] = useState(255);
    const [embroideryMaxCharactersDraft, setEmbroideryMaxCharactersDraft] = useState('255');
    const [defaultsDraft, setDefaultsDraft] = useState<Record<string, string>>({});
    const [defaultModal, setDefaultModal] = useState<{ sectionKey: string; selected: string } | null>(null);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [expandedButtonCategory, setExpandedButtonCategory] = useState<Record<string, boolean>>({});
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // ----- Visibilité des liasses & tissus -----
    const [liasses, setLiasses] = useState<AdminLiasse[]>([]);
    const [serverLiasses, setServerLiasses] = useState<Record<string, boolean>>({});
    const [serverFabrics, setServerFabrics] = useState<Record<string, Record<string, boolean>>>({});
    const [serverDreamBoxLiasses, setServerDreamBoxLiasses] = useState<Record<string, boolean>>({});
    const [serverDreamBoxFabrics, setServerDreamBoxFabrics] = useState<Record<string, Record<string, boolean>>>({});
    const [liassesDraft, setLiassesDraft] = useState<Record<string, boolean>>({});
    const [fabricsDraft, setFabricsDraft] = useState<Record<string, Record<string, boolean>>>({});
    const [dreamBoxLiassesDraft, setDreamBoxLiassesDraft] = useState<Record<string, boolean>>({});
    const [dreamBoxFabricsDraft, setDreamBoxFabricsDraft] = useState<Record<string, Record<string, boolean>>>({});
    const [expandedLiasse, setExpandedLiasse] = useState<Record<string, boolean>>({});
    const [expandedDreamBoxLiasse, setExpandedDreamBoxLiasse] = useState<Record<string, boolean>>({});

    useEffect(() => {
        getAllAdminLiasses().then(setLiasses).catch(() => setLiasses([]));
    }, []);

    const initialLiasses = useMemo<Record<string, boolean>>(() => {
        const map: Record<string, boolean> = {};
        for (const l of liasses) map[l.namespace] = serverLiasses[l.namespace] !== false;
        return map;
    }, [liasses, serverLiasses]);

    const initialFabrics = useMemo<Record<string, Record<string, boolean>>>(() => {
        const map: Record<string, Record<string, boolean>> = {};
        for (const l of liasses) {
            map[l.namespace] = {};
            for (const f of l.items) map[l.namespace][f.id] = serverFabrics[l.namespace]?.[f.id] !== false;
        }
        return map;
    }, [liasses, serverFabrics]);

    const initialDreamBoxLiasses = useMemo(() => Object.fromEntries(
        liasses.map((l) => [l.namespace, serverDreamBoxLiasses[l.namespace] !== false]),
    ) as Record<string, boolean>, [liasses, serverDreamBoxLiasses]);
    const initialDreamBoxFabrics = useMemo(() => Object.fromEntries(
        liasses.map((l) => [l.namespace, Object.fromEntries(l.items.map((f) => [f.id, serverDreamBoxFabrics[l.namespace]?.[f.id] !== false]))]),
    ) as Record<string, Record<string, boolean>>, [liasses, serverDreamBoxFabrics]);

    useEffect(() => { setLiassesDraft(initialLiasses); }, [initialLiasses]);
    useEffect(() => { setFabricsDraft(initialFabrics); }, [initialFabrics]);
    useEffect(() => { setDreamBoxLiassesDraft(initialDreamBoxLiasses); }, [initialDreamBoxLiasses]);
    useEffect(() => { setDreamBoxFabricsDraft(initialDreamBoxFabrics); }, [initialDreamBoxFabrics]);

    const toggleLiasse = useCallback((ns: string) => {
        setLiassesDraft((prev) => ({ ...prev, [ns]: !prev[ns] }));
    }, []);
    const toggleFabric = useCallback((ns: string, id: string) => {
        setFabricsDraft((prev) => {
            const section = prev[ns] || {};
            return { ...prev, [ns]: { ...section, [id]: !(section[id] !== false) } };
        });
    }, []);
    const setLiasseFabricsAll = useCallback((ns: string, visible: boolean) => {
        const l = liasses.find((x) => x.namespace === ns);
        if (!l) return;
        setFabricsDraft((prev) => {
            const next = { ...(prev[ns] || {}) };
            for (const f of l.items) next[f.id] = visible;
            return { ...prev, [ns]: next };
        });
    }, [liasses]);
    const toggleExpandedLiasse = useCallback((ns: string) => {
        setExpandedLiasse((prev) => ({ ...prev, [ns]: !prev[ns] }));
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await getAdminSettings();
                if (cancelled) return;
                setServerSections(res.data.visibleSections || {});
                setServerItems(res.data.visibleItems || {});
                setServerButtonCategories(res.data.visibleButtonCategories || {});
                setServerLiasses(res.data.visibleLiasses || {});
                setServerFabrics(res.data.visibleFabrics || {});
                setServerDreamBoxLiasses(res.data.dreamBoxVisibleLiasses || {});
                setServerDreamBoxFabrics(res.data.dreamBoxVisibleFabrics || {});
                setServerDefaults(res.data.sectionDefaults || {});
                setDefaultsDraft(res.data.sectionDefaults || {});
                const embroideryMax = res.data.embroideryMaxCharacters ?? 255;
                setServerEmbroideryMaxCharacters(embroideryMax);
                setEmbroideryMaxCharactersDraft(String(embroideryMax));
            } catch (err: any) {
                if (!cancelled) setError(err?.message || 'Erreur lors du chargement');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        getButtonCatalog().then(setButtonCatalog).catch(() => { /* catalogue optionnel */ });
        getLiningCatalog().then(setLiningCatalog).catch(() => { /* catalogue optionnel */ });
        getCollarFeltCatalog().then(setCollarFeltCatalog).catch(() => { /* catalogue optionnel */ });
        getContrastColorCatalog().then(setContrastColorCatalog).catch(() => { /* catalogue optionnel */ });
    }, []);

    useEffect(() => {
        setDraft(applyDependencyRules(initial));
        setItemsDraft(initialItems);
        setButtonCategoriesDraft(initialButtonCategories);
    }, [initial, initialItems, initialButtonCategories]);

    const lockedBy = useMemo(() => {
        const map: Record<string, string | null> = {};
        for (const group of ADMIN_SECTION_GROUPS) {
            for (const section of group.sections) {
                map[section.key] = findLockingMaster(section.key, draft);
            }
        }
        return map;
    }, [draft]);

    const isDirty = useMemo(() => {
        if (Object.keys(initial).some((key) => initial[key] !== draft[key])) return true;
        if (JSON.stringify(serverDefaults) !== JSON.stringify(defaultsDraft)) return true;
        if (String(serverEmbroideryMaxCharacters) !== embroideryMaxCharactersDraft) return true;
        if (Object.keys(initialButtonCategories).some((key) => initialButtonCategories[key] !== buttonCategoriesDraft[key])) return true;
        for (const sectionKey of Object.keys(initialItems)) {
            const initSection = initialItems[sectionKey];
            const draftSection = itemsDraft[sectionKey] || {};
            for (const itemId of Object.keys(initSection)) {
                if (initSection[itemId] !== draftSection[itemId]) return true;
            }
        }
        for (const l of liasses) {
            if (initialLiasses[l.namespace] !== liassesDraft[l.namespace]) return true;
            if (initialDreamBoxLiasses[l.namespace] !== dreamBoxLiassesDraft[l.namespace]) return true;
            const initF = initialFabrics[l.namespace] || {};
            const draftF = fabricsDraft[l.namespace] || {};
            const initDreamBoxF = initialDreamBoxFabrics[l.namespace] || {};
            const draftDreamBoxF = dreamBoxFabricsDraft[l.namespace] || {};
            for (const f of l.items) {
                if (initF[f.id] !== draftF[f.id]) return true;
                if (initDreamBoxF[f.id] !== draftDreamBoxF[f.id]) return true;
            }
        }
        return false;
    }, [initial, draft, initialItems, itemsDraft, initialButtonCategories, buttonCategoriesDraft, serverDefaults, defaultsDraft, serverEmbroideryMaxCharacters, embroideryMaxCharactersDraft, liasses, initialLiasses, liassesDraft, initialFabrics, fabricsDraft, initialDreamBoxLiasses, dreamBoxLiassesDraft, initialDreamBoxFabrics, dreamBoxFabricsDraft]);

    const openDefaultModal = useCallback((sectionKey: string) => {
        const current = defaultsDraft[sectionKey] ?? SECTION_DEFAULT_VALUES[sectionKey] ?? sectionItems[sectionKey]?.[0]?.id ?? '';
        setDefaultModal({ sectionKey, selected: current });
    }, [defaultsDraft, sectionItems]);

    const toggle = useCallback((key: string) => {
        if (findLockingMaster(key, draft)) return;
        const nextValue = !draft[key];
        setDraft((prev) => {
            const next = { ...prev, [key]: nextValue };
            if (!nextValue) {
                for (const dep of getDependentsOf(key)) {
                    next[dep] = false;
                }
            }
            return applyDependencyRules(next);
        });
        if (!nextValue && isSectionDefaultable(key)) openDefaultModal(key);
    }, [draft, openDefaultModal]);

    const setGroupAll = useCallback((groupId: string, visible: boolean) => {
        const group = ADMIN_SECTION_GROUPS.find((g) => g.id === groupId);
        if (!group) return;
        setDraft((prev) => {
            const next = { ...prev };
            for (const section of group.sections) {
                if (visible && findLockingMaster(section.key, next)) continue;
                next[section.key] = visible;
                if (!visible) {
                    for (const dep of getDependentsOf(section.key)) {
                        next[dep] = false;
                    }
                }
            }
            return applyDependencyRules(next);
        });
    }, []);

    const toggleItem = useCallback((sectionKey: string, itemId: string) => {
        const wasVisible = itemsDraft[sectionKey]?.[itemId] !== false;
        setItemsDraft((prev) => {
            const section = prev[sectionKey] || {};
            return { ...prev, [sectionKey]: { ...section, [itemId]: !section[itemId] } };
        });
        const currentDefault = defaultsDraft[sectionKey] ?? SECTION_DEFAULT_VALUES[sectionKey];
        if (wasVisible && isSectionDefaultable(sectionKey) && currentDefault === itemId) {
            openDefaultModal(sectionKey);
        }
    }, [itemsDraft, defaultsDraft, openDefaultModal]);

    const setSectionItemsAll = useCallback((sectionKey: string, visible: boolean) => {
        const sectionKeys = [sectionKey];
        setItemsDraft((prev) => {
            const result = { ...prev };
            for (const key of sectionKeys) {
                const items = sectionItems[key] || [];
                const next = { ...(prev[key] || {}) };
                for (const item of items) next[item.id] = visible;
                result[key] = next;
            }
            return result;
        });
        if (isCatalogSection(sectionKey)) {
            setButtonCategoriesDraft((prev) => {
                const next = { ...prev };
                for (const category of catalogForSection(sectionKey)) {
                    next[buttonCategoryVisibilityKey(sectionKey, category.id)] = visible;
                }
                return next;
            });
        }
    }, [sectionItems, catalogForSection]);

    const setButtonCategoryAll = useCallback((sectionKey: string, category: CatalogCategory, visible: boolean) => {
        const key = buttonCategoryVisibilityKey(sectionKey, category.id);
        setButtonCategoriesDraft((prev) => ({ ...prev, [key]: visible }));
    }, []);

    const toggleButtonCategory = useCallback((categoryId: string) => {
        setExpandedButtonCategory((prev) => ({ ...prev, [categoryId]: !prev[categoryId] }));
    }, []);

    const toggleExpanded = useCallback((sectionKey: string) => {
        setExpanded((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
    }, []);

    const handleSave = useCallback(async () => {
        const embroideryMax = Number(embroideryMaxCharactersDraft);
        if (!Number.isInteger(embroideryMax) || embroideryMax < 1 || embroideryMax > 255) {
            setError('La limite de broderie doit être un entier entre 1 et 255.');
            return;
        }
        setSaving(true);
        setMessage(null);
        setError(null);
        try {
            const sectionsPayload: Record<string, boolean> = {};
            for (const [key, visible] of Object.entries(draft)) {
                if (!visible) sectionsPayload[key] = false;
            }
            const itemsPayload: Record<string, Record<string, boolean>> = {};
            for (const [sectionKey, sectionMap] of Object.entries(itemsDraft)) {
                const hidden: Record<string, boolean> = {};
                for (const [itemId, visible] of Object.entries(sectionMap)) {
                    if (!visible) hidden[itemId] = false;
                }
                if (Object.keys(hidden).length > 0) itemsPayload[sectionKey] = hidden;
            }
            const buttonCategoriesPayload: Record<string, boolean> = {};
            for (const [categoryId, visible] of Object.entries(buttonCategoriesDraft)) {
                if (!visible) buttonCategoriesPayload[categoryId] = false;
            }
            const sectionsRes = await updateAdminVisibleSections(mirrorVestValues(sectionsPayload));
            const itemsRes = await updateAdminVisibleItems(mirrorVestItems(itemsPayload), mirrorVestCategories(buttonCategoriesPayload));
            const defaultsRes = await updateAdminSectionDefaults(mirrorVestValues(defaultsDraft));

            const liassesPayload: Record<string, boolean> = {};
            for (const [ns, visible] of Object.entries(liassesDraft)) {
                if (!visible) liassesPayload[ns] = false;
            }
            const fabricsPayload: Record<string, Record<string, boolean>> = {};
            for (const [ns, map] of Object.entries(fabricsDraft)) {
                const hidden: Record<string, boolean> = {};
                for (const [id, visible] of Object.entries(map)) {
                    if (!visible) hidden[id] = false;
                }
                if (Object.keys(hidden).length > 0) fabricsPayload[ns] = hidden;
            }
            const dreamBoxVisibleLiasses = Object.fromEntries(Object.entries(dreamBoxLiassesDraft).filter(([, visible]) => !visible));
            const dreamBoxVisibleFabrics = Object.fromEntries(Object.entries(dreamBoxFabricsDraft)
                .map(([ns, items]) => [ns, Object.fromEntries(Object.entries(items).filter(([, visible]) => !visible))])
                .filter(([, items]) => Object.keys(items).length > 0));
            const fabricsRes = await updateAdminVisibleFabrics({ visibleLiasses: liassesPayload, visibleFabrics: fabricsPayload, dreamBoxVisibleLiasses, dreamBoxVisibleFabrics });
            const embroideryRes = await updateAdminEmbroiderySettings(embroideryMax);

            setServerSections(sectionsRes.data.visibleSections || {});
            setServerItems(itemsRes.data.visibleItems || {});
            setServerButtonCategories(itemsRes.data.visibleButtonCategories || {});
            setServerDefaults(defaultsRes.data.sectionDefaults || {});
            setServerLiasses(fabricsRes.data.visibleLiasses || {});
            setServerFabrics(fabricsRes.data.visibleFabrics || {});
            setServerDreamBoxLiasses(fabricsRes.data.dreamBoxVisibleLiasses || {});
            setServerDreamBoxFabrics(fabricsRes.data.dreamBoxVisibleFabrics || {});
            setServerEmbroideryMaxCharacters(embroideryRes.data.embroideryMaxCharacters);
            setMessage('Réglages enregistrés');
        } catch (err: any) {
            setError(err?.message || 'Erreur lors de l’enregistrement');
        } finally {
            setSaving(false);
        }
    }, [draft, itemsDraft, buttonCategoriesDraft, defaultsDraft, embroideryMaxCharactersDraft, liassesDraft, fabricsDraft, dreamBoxLiassesDraft, dreamBoxFabricsDraft]);

    const handleReset = useCallback(() => {
        setDraft(applyDependencyRules(initial));
        setItemsDraft(initialItems);
        setButtonCategoriesDraft(initialButtonCategories);
        setDefaultsDraft(serverDefaults);
        setEmbroideryMaxCharactersDraft(String(serverEmbroideryMaxCharacters));
        setLiassesDraft(initialLiasses);
        setFabricsDraft(initialFabrics);
        setDreamBoxLiassesDraft(initialDreamBoxLiasses);
        setDreamBoxFabricsDraft(initialDreamBoxFabrics);
        setMessage(null);
        setError(null);
    }, [initial, initialItems, initialButtonCategories, serverDefaults, serverEmbroideryMaxCharacters, initialLiasses, initialFabrics, initialDreamBoxLiasses, initialDreamBoxFabrics]);

    return (
        <Layout>
            <div className="h-full overflow-y-auto">
                <div className="max-w-3xl mx-auto px-6 py-10">
                    <h1 className="text-xl font-bold text-gray-900 mb-1">Visibilité globale et broderie</h1>
                    <p className="text-sm text-gray-500 mb-6">
                        Les sections et items masqués ici sont invisibles pour tous les comptes membres,
                        quelle que soit leur configuration personnelle.
                    </p>

                    {message && (
                        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl p-3 mb-5">
                            <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                            <p className="text-sm text-green-700">{message}</p>
                        </div>
                    )}

                    {error && (
                        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3 mb-5">
                            <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    )}

                    {loading ? (
                        <p className="text-sm text-gray-400">Chargement…</p>
                    ) : (
                        <div className="space-y-5">
                            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm px-6 py-5">
                                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Broderie</h2>
                                <label htmlFor="embroidery-max-characters" className="block mt-4 text-sm font-medium text-gray-700">Nombre maximal de caractères par broderie</label>
                                <input id="embroidery-max-characters" type="number" min={1} max={255} step={1} value={embroideryMaxCharactersDraft}
                                    onChange={(event) => setEmbroideryMaxCharactersDraft(event.target.value)}
                                    className="mt-2 w-32 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-[#047857] focus:outline-none" />
                                <p className="mt-2 text-xs text-gray-500">De 1 à 255 caractères. Cette limite s’applique à la veste, au pantalon et au gilet, pour tous les membres.</p>
                            </div>
                            {ADMIN_SECTION_GROUPS.map((group) => {
                                const visibleCount = group.sections.filter((s) => draft[s.key]).length;
                                const allVisible = visibleCount === group.sections.length;
                                return (
                                    <div key={group.id} className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                                            <div>
                                                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">{group.id === 'standaloneVestConfiguration' ? 'Gilet (costume 3 pièces et gilet seul)' : labelSection(group.titleKey)}</h2>
                                                <p className="text-xs text-gray-400 mt-0.5">{visibleCount} / {group.sections.length} visibles</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setGroupAll(group.id, !allVisible)}
                                                className="text-xs font-medium text-[#047857] hover:text-[#035C43]"
                                            >
                                                {allVisible ? 'Tout masquer' : 'Tout afficher'}
                                            </button>
                                        </div>
                                        <ul className="divide-y divide-gray-100">
                                            {group.sections.map((section) => {
                                                const locked = lockedBy[section.key];
                                                const visible = !locked && draft[section.key];
                                                const items: SectionItem[] = sectionItems[section.key] || [];
                                                const hasItems = items.length > 0;
                                                const isOpen = !!expanded[section.key];
                                                const sectionItemsMap = itemsDraft[section.key] || {};
                                                const itemsVisibleCount = isCatalogSection(section.key)
                                                    ? items.filter((item) => {
                                                        if (item.id === 'none' || item.id === 'recouvert') return sectionItemsMap[item.id] !== false;
                                                        const category = catalogForSection(section.key).find((candidate) => candidate.options.some((option) => option.id === item.id));
                                                        const categoryKey = category ? buttonCategoryVisibilityKey(section.key, category.id) : null;
                                                        return (!categoryKey || buttonCategoriesDraft[categoryKey] !== false) && sectionItemsMap[item.id] !== false;
                                                    }).length
                                                    : items.filter((item) => sectionItemsMap[item.id] !== false).length;
                                                const itemsTotal = items.length;
                                                const itemsAllVisible = hasItems && itemsVisibleCount === itemsTotal;
                                                const itemsCanInteract = hasItems && !locked && visible;
                                                return (
                                                    <li key={section.key} className={`${locked ? 'bg-gray-50/60' : ''}`}>
                                                        <div className="flex items-center gap-4 px-6 py-3.5">
                                                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${visible ? 'bg-[#047857]/10' : 'bg-gray-100'}`}>
                                                                {locked
                                                                    ? <LockClosedIcon className="w-4 h-4 text-gray-400" />
                                                                    : visible
                                                                        ? <EyeIcon className="w-4 h-4 text-[#047857]" />
                                                                        : <EyeSlashIcon className="w-4 h-4 text-gray-400" />}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => itemsCanInteract && toggleExpanded(section.key)}
                                                                disabled={!itemsCanInteract}
                                                                className={`flex-1 min-w-0 text-left ${itemsCanInteract ? 'cursor-pointer' : 'cursor-default'}`}
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <p className={`text-sm font-medium ${visible ? 'text-gray-900' : 'text-gray-500'}`}>
                                                                        {section.key === 'coupe.addVest' ? 'Coupe du gilet' : labelSection(section.titleKey)}
                                                                    </p>
                                                                    {hasItems && (
                                                                        <span className="text-[11px] text-gray-400 px-1.5 py-0.5 bg-gray-100 rounded-md">
                                                                            {itemsVisibleCount}/{itemsTotal}
                                                                        </span>
                                                                    )}
                                                                    {itemsCanInteract && (
                                                                        <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                                                    )}
                                                                </div>
                                                                {locked && (
                                                                    <p className="text-xs text-amber-600 mt-0.5 truncate">
                                                                        Verrouillé par « {labelSection(
                                                                            ADMIN_SECTION_GROUPS
                                                                                .flatMap((g) => g.sections)
                                                                                .find((s) => s.key === locked)?.titleKey,
                                                                            locked || undefined,
                                                                        )} »
                                                                    </p>
                                                                )}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                role="switch"
                                                                aria-checked={visible}
                                                                disabled={!!locked}
                                                                onClick={() => toggle(section.key)}
                                                                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${visible ? 'bg-[#047857]' : 'bg-gray-300'} ${locked ? 'opacity-40 cursor-not-allowed' : ''}`}
                                                            >
                                                                <span
                                                                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow ${visible ? 'translate-x-5' : 'translate-x-0.5'}`}
                                                                />
                                                            </button>
                                                        </div>
                                                        {itemsCanInteract && isOpen && (
                                                            <div className="bg-gray-50/50 border-t border-gray-100 px-6 py-3">
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                                                        {isCatalogSection(section.key) ? 'Catégories et références' : 'Items'}
                                                                    </p>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setSectionItemsAll(section.key, !itemsAllVisible)}
                                                                        className="text-[11px] font-medium text-[#047857] hover:text-[#035C43]"
                                                                    >
                                                                        {itemsAllVisible ? 'Tout masquer' : 'Tout afficher'}
                                                                    </button>
                                                                </div>
                                                                {isCatalogSection(section.key) ? (
                                                                    <div className="space-y-3">
                                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-lg border border-gray-200 bg-white p-3">
                                                                            {items.filter((item) => item.id === 'none' || item.id === 'recouvert' || item.id === 'match' || (section.key === COLLAR_FELT_SECTION_KEY && item.id === 'other') || (CONTRAST_SECTION_KEYS.includes(section.key) && SECTION_ITEMS[section.key]?.some((position) => position.id === item.id))).map((item) => {
                                                                                const itemVisible = sectionItemsMap[item.id] !== false;
                                                                                const label = item.labelKey ? labelItem(item.labelKey, item.label) : (item.label || item.id);
                                                                                return (
                                                                                    <div key={item.id} className="flex items-center gap-2">
                                                                                        <button
                                                                                            type="button"
                                                                                            role="switch"
                                                                                            aria-checked={itemVisible}
                                                                                            onClick={() => toggleItem(section.key, item.id)}
                                                                                            className={`relative inline-flex h-4 w-7 flex-shrink-0 items-center rounded-full transition-colors ${itemVisible ? 'bg-[#047857]' : 'bg-gray-300'}`}
                                                                                        >
                                                                                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform shadow ${itemVisible ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                                                                                        </button>
                                                                                        <span className={`text-xs truncate ${itemVisible ? 'text-gray-700' : 'text-gray-400 line-through'}`}>
                                                                                            {label}
                                                                                        </span>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>

                                                                        {catalogForSection(section.key).map((category) => {
                                                                            const categoryKey = buttonCategoryVisibilityKey(section.key, category.id);
                                                                            const categoryVisible = buttonCategoriesDraft[categoryKey] !== false;
                                                                            const categoryVisibleCount = categoryVisible
                                                                                ? category.options.filter((option) => sectionItemsMap[option.id] !== false).length
                                                                                : 0;
                                                                            const categoryOpen = !!expandedButtonCategory[categoryKey];
                                                                            return (
                                                                                <div key={category.id} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                                                                                    <div className="flex items-center gap-3 px-3 py-2.5">
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => toggleButtonCategory(categoryKey)}
                                                                                            className="flex min-w-0 flex-1 items-center gap-2 text-left"
                                                                                        >
                                                                                            <span className="text-xs font-semibold text-gray-800">{labelCatalogCategory(category)}</span>
                                                                                            <span className="text-[10px] text-gray-400">{categoryVisibleCount}/{category.options.length}</span>
                                                                                            <ChevronDownIcon className={`h-3.5 w-3.5 text-gray-400 transition-transform ${categoryOpen ? 'rotate-180' : ''}`} />
                                                                                        </button>
                                                                                        <button
                                                                                            type="button"
                                                                                            role="switch"
                                                                                            aria-label={`Visibilité de la catégorie ${labelCatalogCategory(category)}`}
                                                                                            aria-checked={categoryVisible}
                                                                                            onClick={() => setButtonCategoryAll(section.key, category, !categoryVisible)}
                                                                                            className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${categoryVisible ? 'bg-[#047857]' : 'bg-gray-300'}`}
                                                                                        >
                                                                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${categoryVisible ? 'translate-x-4' : 'translate-x-0.5'}`} />
                                                                                        </button>
                                                                                    </div>
                                                                                    {categoryOpen && (
                                                                                        <ul className={`grid grid-cols-1 gap-2 border-t border-gray-100 bg-gray-50/60 p-3 sm:grid-cols-2 ${categoryVisible ? '' : 'opacity-50'}`}>
                                                                                            {category.options.map((option) => {
                                                                                                const optionVisible = categoryVisible && sectionItemsMap[option.id] !== false;
                                                                                                return (
                                                                                                    <li key={option.id} className="flex items-center gap-2 rounded-lg bg-white p-2">
                                                                                                        <img src={option.image} alt="" loading="lazy" className="h-9 w-9 rounded-md border border-gray-100 object-cover" />
                                                                                                        <span className={`min-w-0 flex-1 truncate text-xs ${optionVisible ? 'text-gray-700' : 'text-gray-400 line-through'}`} title={option.name}>
                                                                                                            {option.name}
                                                                                                        </span>
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            role="switch"
                                                                                                            aria-label={`Visibilité de la référence ${option.name}`}
                                                                                                            aria-checked={optionVisible}
                                                                                                            disabled={!categoryVisible}
                                                                                                            onClick={() => toggleItem(section.key, option.id)}
                                                                                                            className={`relative inline-flex h-4 w-7 flex-shrink-0 items-center rounded-full transition-colors ${optionVisible ? 'bg-[#047857]' : 'bg-gray-300'} ${categoryVisible ? '' : 'cursor-not-allowed'}`}
                                                                                                        >
                                                                                                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${optionVisible ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                                                                                                        </button>
                                                                                                    </li>
                                                                                                );
                                                                                            })}
                                                                                        </ul>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (
                                                                    <div className="space-y-3">
                                                                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                                                            {items.map((item) => {
                                                                                const itemVisible = sectionItemsMap[item.id] !== false;
                                                                                const label = item.labelKey ? labelItem(item.labelKey, item.label) : (item.label || item.id);
                                                                                return (
                                                                                    <li key={item.id} className="flex items-center gap-2 py-1">
                                                                                        <button
                                                                                            type="button"
                                                                                            role="switch"
                                                                                            aria-checked={itemVisible}
                                                                                            onClick={() => toggleItem(section.key, item.id)}
                                                                                            className={`relative inline-flex h-4 w-7 flex-shrink-0 items-center rounded-full transition-colors ${itemVisible ? 'bg-[#047857]' : 'bg-gray-300'}`}
                                                                                        >
                                                                                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform shadow ${itemVisible ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                                                                                        </button>
                                                                                        <span className={`text-xs truncate ${itemVisible ? 'text-gray-700' : 'text-gray-400 line-through'}`}>
                                                                                            {label}
                                                                                        </span>
                                                                                    </li>
                                                                                );
                                                                            })}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Liasses & tissus */}
                    <div className="mt-5">
                        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-100">
                                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Liasses &amp; tissus</h2>
                                <p className="text-xs text-gray-400 mt-0.5">Masquez des liasses ou des tissus pour tous les membres.</p>
                            </div>
                            {liasses.length === 0 ? (
                                <div className="px-6 py-4 text-sm text-gray-400">Chargement…</div>
                            ) : (
                                <ul className="divide-y divide-gray-100">
                                    {liasses.map((l) => {
                                        const liasseVisible = liassesDraft[l.namespace] !== false;
                                        const isOpen = !!expandedLiasse[l.namespace];
                                        const fabricsMap = fabricsDraft[l.namespace] || {};
                                        const fabricsVisibleCount = l.items.filter((f) => fabricsMap[f.id] !== false).length;
                                        const fabricsAllVisible = l.items.length > 0 && fabricsVisibleCount === l.items.length;
                                        const canInteract = liasseVisible && l.items.length > 0;
                                        return (
                                            <li key={l.namespace}>
                                                <div className="flex items-center gap-4 px-6 py-3.5">
                                                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${liasseVisible ? 'bg-[#047857]/10' : 'bg-gray-100'}`}>
                                                        {liasseVisible ? <EyeIcon className="w-4 h-4 text-[#047857]" /> : <EyeSlashIcon className="w-4 h-4 text-gray-400" />}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => canInteract && toggleExpandedLiasse(l.namespace)}
                                                        disabled={!canInteract}
                                                        className={`flex-1 min-w-0 text-left ${canInteract ? 'cursor-pointer' : 'cursor-default'}`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <p className={`text-sm font-medium ${liasseVisible ? 'text-gray-900' : 'text-gray-500'}`}>{l.namespaceLabel}</p>
                                                            <span className="text-[11px] text-gray-400 px-1.5 py-0.5 bg-gray-100 rounded-md">{fabricsVisibleCount}/{l.items.length}</span>
                                                            {canInteract && (
                                                                <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                                            )}
                                                        </div>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        role="switch"
                                                        aria-checked={liasseVisible}
                                                        onClick={() => toggleLiasse(l.namespace)}
                                                        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${liasseVisible ? 'bg-[#047857]' : 'bg-gray-300'}`}
                                                    >
                                                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow ${liasseVisible ? 'translate-x-5' : 'translate-x-0.5'}`} />
                                                    </button>
                                                </div>
                                                {canInteract && isOpen && (
                                                    <div className="bg-gray-50/50 border-t border-gray-100 px-6 py-3">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Tissus disponibles</p>
                                                            <button
                                                                type="button"
                                                                onClick={() => setLiasseFabricsAll(l.namespace, !fabricsAllVisible)}
                                                                className="text-[11px] font-medium text-[#047857] hover:text-[#035C43]"
                                                            >
                                                                {fabricsAllVisible ? 'Tout masquer' : 'Tout afficher'}
                                                            </button>
                                                        </div>
                                                        <ul className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                                                            {l.items.map((f) => {
                                                                const fabricVisible = fabricsMap[f.id] !== false;
                                                                return (
                                                                    <li key={f.id} className="flex items-center gap-2 py-1">
                                                                        <button
                                                                            type="button"
                                                                            role="switch"
                                                                            aria-checked={fabricVisible}
                                                                            onClick={() => toggleFabric(l.namespace, f.id)}
                                                                            className={`relative inline-flex h-4 w-7 flex-shrink-0 items-center rounded-full transition-colors ${fabricVisible ? 'bg-[#047857]' : 'bg-gray-300'}`}
                                                                        >
                                                                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform shadow ${fabricVisible ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                                                                        </button>
                                                                        <span className={`text-xs truncate ${fabricVisible ? 'text-gray-700' : 'text-gray-400 line-through'}`}>{f.id}</span>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    </div>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden mt-6">
                        <div className="px-6 py-4 border-b border-gray-100">
                            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">DREAM BOX — liasses et tissus</h2>
                            <p className="text-xs text-gray-400 mt-0.5">Choix disponibles dans la DREAM BOX pour tous les membres. Les masquages généraux s'appliquent aussi.</p>
                        </div>
                        <ul className="divide-y divide-gray-100">
                            {liasses.map((l) => {
                                const visible = dreamBoxLiassesDraft[l.namespace] !== false;
                                const open = expandedDreamBoxLiasse[l.namespace] === true;
                                const allowed = l.items.filter((f) => fabricsDraft[l.namespace]?.[f.id] !== false);
                                const count = allowed.filter((f) => dreamBoxFabricsDraft[l.namespace]?.[f.id] !== false).length;
                                return <li key={l.namespace}>
                                    <div className="flex items-center gap-3 px-6 py-3">
                                        <button type="button" onClick={() => setExpandedDreamBoxLiasse((previous) => ({ ...previous, [l.namespace]: !open }))} className="flex-1 text-left text-sm font-medium text-gray-800">
                                            {l.namespaceLabel} <span className="text-xs text-gray-400">{count}/{allowed.length}</span>
                                        </button>
                                        <button type="button" role="switch" aria-label={`DREAM BOX ${l.namespaceLabel}`} aria-checked={visible}
                                            onClick={() => setDreamBoxLiassesDraft((previous) => ({ ...previous, [l.namespace]: !visible }))}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full ${visible ? 'bg-[#047857]' : 'bg-gray-300'}`}>
                                            <span className={`h-5 w-5 rounded-full bg-white shadow transition-transform ${visible ? 'translate-x-5' : 'translate-x-0.5'}`} />
                                        </button>
                                    </div>
                                    {open && visible && <div className="border-t border-gray-100 bg-gray-50 px-6 py-3">
                                        <button type="button" className="mb-3 text-xs text-[#047857]" onClick={() => setDreamBoxFabricsDraft((previous) => ({ ...previous, [l.namespace]: { ...previous[l.namespace], ...Object.fromEntries(allowed.map((f) => [f.id, count !== allowed.length])) } }))}>
                                            {count === allowed.length ? 'Tout masquer' : 'Tout afficher'}
                                        </button>
                                        <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {allowed.map((f) => {
                                                const fabricVisible = dreamBoxFabricsDraft[l.namespace]?.[f.id] !== false;
                                                return <li key={f.id}><label className="flex items-center gap-2 text-xs text-gray-700">
                                                    <input type="checkbox" checked={fabricVisible} onChange={() => setDreamBoxFabricsDraft((previous) => ({ ...previous, [l.namespace]: { ...previous[l.namespace], [f.id]: !fabricVisible } }))} />
                                                    {f.id}
                                                </label></li>;
                                            })}
                                        </ul>
                                    </div>}
                                </li>;
                            })}
                        </ul>
                    </div>

                    <div className="flex gap-3 mt-8 sticky bottom-0 bg-white/80 backdrop-blur py-4 -mx-6 px-6 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={!isDirty || saving || loading}
                            className="flex-1 py-2.5 bg-[#047857] text-white text-sm font-medium rounded-xl hover:bg-[#035C43] transition-colors disabled:opacity-50"
                        >
                            {saving ? 'Enregistrement…' : 'Enregistrer'}
                        </button>
                        <button
                            type="button"
                            onClick={handleReset}
                            disabled={!isDirty || saving}
                            className="flex-1 py-2.5 border border-gray-200 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            Annuler
                        </button>
                    </div>
                </div>
            </div>

            {defaultModal && (() => {
                const items = sectionItems[defaultModal.sectionKey] || [];
                const sectionTitleKey = ADMIN_SECTION_GROUPS
                    .flatMap((g) => g.sections)
                    .find((s) => s.key === defaultModal.sectionKey)?.titleKey;
                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
                            <div className="px-6 py-4 border-b border-gray-100">
                                <h3 className="text-sm font-semibold text-gray-900">Valeur par défaut</h3>
                                <p className="text-xs text-gray-500 mt-1">
                                    « {labelSection(sectionTitleKey)} » sera masquée. Choisissez la valeur utilisée par défaut dans les commandes pour éviter de les bloquer.
                                </p>
                            </div>
                            <div className="flex-1 overflow-y-auto px-3 py-2">
                                <ul className="space-y-1">
                                    {items.map((item) => {
                                        const label = item.labelKey ? labelItem(item.labelKey, item.label) : (item.label || item.id);
                                        const selected = defaultModal.selected === item.id;
                                        return (
                                            <li key={item.id}>
                                                <button
                                                    type="button"
                                                    onClick={() => setDefaultModal({ ...defaultModal, selected: item.id })}
                                                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm ${selected ? 'bg-[#047857]/10 text-[#047857] font-medium' : 'hover:bg-gray-50 text-gray-700'}`}
                                                >
                                                    {label}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                            <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setDefaultsDraft((p) => ({ ...p, [defaultModal.sectionKey]: defaultModal.selected }));
                                        setDefaultModal(null);
                                    }}
                                    disabled={!defaultModal.selected}
                                    className="flex-1 py-2.5 bg-[#047857] text-white text-sm font-medium rounded-xl hover:bg-[#035C43] transition-colors disabled:opacity-50"
                                >
                                    Confirmer
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setDefaultModal(null)}
                                    className="flex-1 py-2.5 border border-gray-200 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
                                >
                                    Annuler
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </Layout>
    );
};
